import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { classifyRoute, hasAccessBlock, isAdmin, pendingInvitesCount } from "@/lib/guards";
import { usePwaContext } from "@/contexts/PwaContext";
import { PWA } from "@/constants/testIds/pwa";
import AccountSwitcher from "@/components/account/AccountSwitcher";
import { SWITCHER } from "@/constants/testIds/profile";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  BookMarked,
  UserRound,
  LogOut,
  ShieldCheck,
  ChevronRight,
  Sparkles,
  Download,
} from "lucide-react";

const PLAN_LABELS = { free: "Gratuito", pro: "Pro", premium: "Premium" };

// Mobile-first bottom tab bar (FR27 / Story 4.2). Visible only for logged-in,
// allowed users on the main app screens, and only below `md` (the desktop header
// keeps its pill nav). Fixed to the bottom with iOS safe-area inset.
export default function BottomNav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);
  const pwa = usePwaContext();

  const cls = classifyRoute(loc.pathname);
  // Only the main logged app surfaces get the bottom bar; keep onboarding and
  // builder flows immersive, and never show it to guests or gated accounts.
  if (!user || hasAccessBlock(user) || !(cls.isApp || cls.isAdmin || cls.isProfile)) return null;

  const pendingInvites = pendingInvitesCount(user);
  const accountActive = accountOpen || cls.isProfile;

  const items = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Painel", testid: "bottomnav-dashboard" },
    { to: "/marca", icon: Sparkles, label: "Brand", ariaLabel: "BrandPersona", testid: "bottomnav-marca" },
    { to: "/avatars", icon: Users, label: "Avatares", testid: "bottomnav-avatars" },
    { to: "/calendario", icon: CalendarDays, label: "Calendário", testid: "bottomnav-calendar" },
    { to: "/library", icon: BookMarked, label: "Biblioteca", testid: "bottomnav-library" },
  ];

  const isActive = (to) =>
    loc.pathname === to || loc.pathname.startsWith(to + "/");

  const handleLogout = () => {
    setAccountOpen(false);
    logout();
    nav("/");
  };

  const planLabel = PLAN_LABELS[user.plan] || "Gratuito";

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-brand-surface/95 backdrop-blur-xl border-t border-brand-line"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-6 h-16">
          {items.map(({ to, icon: Icon, label, ariaLabel, testid }) => {
            const active = isActive(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                aria-label={ariaLabel || label}
                aria-current={active ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-1 min-h-[44px] min-w-0 px-0.5 transition-colors ${
                  active ? "text-brand-rose" : "text-brand-ink2 hover:text-brand-ink"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="hidden min-[360px]:block text-[10px] font-ui tracking-wide whitespace-nowrap">{label}</span>
              </Link>
            );
          })}
          <button
            type="button"
            data-testid="bottomnav-account"
            onClick={() => setAccountOpen(true)}
            aria-label="Conta"
            aria-haspopup="dialog"
            className={`relative flex flex-col items-center justify-center gap-1 min-h-[44px] min-w-0 px-0.5 transition-colors ${
              accountActive ? "text-brand-rose" : "text-brand-ink2 hover:text-brand-ink"
            }`}
          >
            <span className="relative">
              <UserRound className="w-5 h-5" />
              {pendingInvites > 0 && (
                <span
                  data-testid={SWITCHER.pendingDot}
                  aria-label={`${pendingInvites} convite(s) pendente(s)`}
                  className="absolute -top-1 -right-1.5 min-w-[8px] h-2 w-2 rounded-full bg-brand-rose ring-2 ring-brand-surface"
                />
              )}
            </span>
            <span className="hidden min-[360px]:block text-[10px] font-ui tracking-wide whitespace-nowrap">Conta</span>
          </button>
        </div>
      </nav>

      <Sheet open={accountOpen} onOpenChange={setAccountOpen}>
        <SheetContent
          side="bottom"
          data-testid="account-sheet"
          className="bg-brand-surface border-brand-line rounded-t-3xl"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-2xl text-brand-ink">
              {user.name || "Minha conta"}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-2 space-y-4">
            <div className="space-y-1">
              {user.email && (
                <div className="text-[13.5px] text-brand-ink2 font-ui" data-testid="account-email">
                  {user.email}
                </div>
              )}
              <div className="inline-flex items-center gap-2 mt-1">
                <span className="text-[10.5px] tracking-[0.2em] uppercase text-brand-ink3 font-ui">
                  Plano
                </span>
                <span
                  data-testid="account-plan"
                  className="px-2.5 py-0.5 rounded-full bg-brand-elevated text-brand-rose text-[11px] font-ui"
                >
                  {planLabel}
                </span>
              </div>
            </div>

            {/* Account switcher: own + shared workspaces (tap = 2nd tap to switch) */}
            <AccountSwitcher onSwitched={() => setAccountOpen(false)} />

            <div className="pt-2 border-t border-brand-line flex flex-col gap-2">
              {pwa && !pwa.standalone && (
                <button
                  type="button"
                  data-testid={PWA.accountInstall}
                  onClick={() => { setAccountOpen(false); pwa.onInstall(); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-brand-elevated text-brand-ink hover:text-brand-rose transition-colors min-h-[44px]"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-[14px] font-ui flex-1 text-left">Instalar app</span>
                  <ChevronRight className="w-4 h-4 text-brand-ink3" />
                </button>
              )}
              <Link
                to="/marca"
                data-testid="account-marca-link"
                onClick={() => setAccountOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-brand-elevated text-brand-ink hover:text-brand-rose transition-colors min-h-[44px]"
              >
                <Sparkles className="w-4 h-4" />
                <span className="text-[14px] font-ui flex-1">Minha BrandPersona</span>
                <ChevronRight className="w-4 h-4 text-brand-ink3" />
              </Link>
              <Link
                to="/perfil"
                data-testid="account-profile-link"
                onClick={() => setAccountOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-brand-elevated text-brand-ink hover:text-brand-rose transition-colors min-h-[44px]"
              >
                <UserRound className="w-4 h-4" />
                <span className="text-[14px] font-ui flex-1">Minha conta</span>
                {pendingInvites > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-brand-rose/15 text-brand-rose text-[11px] font-ui">
                    {pendingInvites} convite{pendingInvites === 1 ? "" : "s"}
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-brand-ink3" />
              </Link>
              {isAdmin(user) && (
                <Link
                  to="/admin"
                  data-testid="account-admin-link"
                  onClick={() => setAccountOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-brand-elevated text-brand-ink hover:text-brand-rose transition-colors min-h-[44px]"
                >
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-[14px] font-ui">Painel admin</span>
                </Link>
              )}
              <button
                type="button"
                data-testid="account-logout"
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-brand-elevated text-brand-ink hover:text-brand-rose transition-colors min-h-[44px]"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-[14px] font-ui">Sair</span>
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
